import ProductConfigurationService from "@/core/services/product-configuration.service.js";

const ROUTE_LABELS = {
  "/dashboard": "Dashboard",
  "/patient/list": "Daftar Pasien",
  "/patient/add": "Tambah Pasien",
  "/appointment/list/0": "Semua Pendaftaran",
  "/appointment/add": "Pendaftaran Baru",
  "/appointment/todays": "Pendaftaran Hari Ini",
  "/appointment/upcoming": "Perjanjian Akan Datang",
  "/appointment/calendar": "Kalender Perjanjian",
  "/queue": "Dashboard Antrean",
  "/queue/display": "Display Antrean",
  "/queue/settings": "Pengaturan Loket",
  "/queue/report": "Laporan Antrean",
  "/medicine/list": "Daftar Obat & Alkes",
  "/medicine/control-center": "Kontrol Farmasi & Stok",
  "/pharmacy/control-center": "Pusat Kontrol Farmasi",
  "/pharmacy/sales": "Penjualan Farmasi",
  "/payment/list": "Daftar Pembayaran",
  "/payment/add": "Buat Pembayaran",
  "/report": "Pusat Laporan",
  "/guide": "User Guide Medhop",
  "/help": "Pusat Bantuan",
  "/finance": "Finance Center",
  "/quality": "Mutu & Keselamatan Pasien",
  "/home-care": "Home Care",
  "/inpatient": "Rawat Inap",
  "/bpjs": "Pusat BPJS",
  "/satu-sehat/dashboard": "Dashboard SATUSEHAT",
  "/user-managements/user-access": "Pengguna & Hak Akses",
  "/settings/quick-access": "Pengaturan Akses Cepat",
};

const ROUTE_KEYWORDS = {
  "/patient/list": "pasien cari identitas rekam medis nik",
  "/patient/add": "pasien baru tambah registrasi daftar",
  "/appointment/list/0": "pendaftaran perjanjian appointment booking daftar",
  "/appointment/add": "buat tambah booking jadwal pasien",
  "/queue": "antrean antrian panggil loket poli",
  "/medicine/control-center": "stok batch kedaluwarsa farmasi obat",
  "/pharmacy/sales": "apotek resep penjualan obat",
  "/payment/list": "kasir invoice pembayaran transaksi",
  "/report": "laporan analitik statistik export",
  "/guide": "panduan tutorial cara user guide bantuan",
};

const EXCLUDED_PATHS = [/^\/(?:login|register|reset-password|invitation|404|access-forbidden)(?:\/|$)/, /^\/verify-/];

function storedAccessCodes() {
  try {
    const value = JSON.parse(localStorage.getItem("access_right_display"));
    return Array.isArray(value) ? value.map(String) : [];
  } catch (_) {
    return [];
  }
}

function joinPath(parent, child) {
  if (!child) return parent || "/";
  if (child.charAt(0) === "/") return child;
  const base = (parent || "").replace(/\/$/, "");
  return `${base}/${child}`.replace(/\/+/g, "/") || "/";
}

function flattenRoutes(routes, parentPath = "", inheritedMeta = [], result = []) {
  (routes || []).forEach(record => {
    const path = joinPath(parentPath, record.path);
    const metas = inheritedMeta.concat(record.meta || {});
    result.push({ record, path, metas });
    if (record.children) flattenRoutes(record.children, path, metas, result);
  });
  return result;
}

function allowedByMeta(metas, accessCodes) {
  return metas.every(meta => {
    const required = meta.access_right;
    const any = meta.access_right_any || [];
    const capabilities = meta.capability_any || [];
    if (required && accessCodes.indexOf(String(required)) === -1) return false;
    if (any.length && !any.some(code => accessCodes.indexOf(String(code)) !== -1)) return false;
    if (capabilities.length && !ProductConfigurationService.hasAny(capabilities)) return false;
    return true;
  });
}

function titleFor(item, target) {
  if (ROUTE_LABELS[target]) return ROUTE_LABELS[target];
  const meta = item.record.meta || {};
  const title = String(meta.title || "").split("|").pop().trim();
  if (title && title !== "Medhop") return title;
  return String(item.record.name || target.split("/").filter(Boolean).pop() || "Menu")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, character => character.toUpperCase());
}

function isNavigable(path) {
  return path && path !== "/" && path !== "*" && path.indexOf(":") === -1 &&
    !EXCLUDED_PATHS.some(pattern => pattern.test(path));
}

function samePage(left, right) {
  return String(left || "").split("?")[0].replace(/\/$/, "") ===
    String(right || "").split("?")[0].replace(/\/$/, "");
}

export function accessibleAimeMenus(router, serverAccessCodes = null) {
  const accessCodes = Array.isArray(serverAccessCodes) ? serverAccessCodes.map(String) : storedAccessCodes();
  const consolidated = localStorage.getItem("medhop_branch_id") === "all";
  const consolidatedPaths = ["/dashboard", "/report", "/branch-network", "/help", "/guide"];
  const seen = {};

  return flattenRoutes(router.options.routes).reduce((menus, item) => {
    let target = item.path;
    if (typeof item.record.redirect === "string") target = item.record.redirect;
    if (!isNavigable(target) || !allowedByMeta(item.metas, accessCodes)) return menus;
    if (consolidated && !consolidatedPaths.some(path => target === path || target.indexOf(`${path}/`) === 0)) return menus;
    if (seen[target]) return menus;
    seen[target] = true;
    menus.push({
      label: titleFor(item, target),
      route: target,
      keywords: ROUTE_KEYWORDS[target] || "",
      accessCodes: item.metas.reduce((codes, meta) => codes.concat(meta.access_right || [], meta.access_right_any || []), []).map(String),
    });
    return menus;
  }, []);
}

function tokenize(value) {
  return String(value || "").toLocaleLowerCase("id-ID").replace(/[^a-z0-9]+/g, " ").trim().split(/\s+/).filter(word => word.length > 1);
}

export function searchAimeMenus(router, question, serverAccessCodes = null, limit = 4) {
  const query = String(question || "").toLocaleLowerCase("id-ID");
  const tokens = tokenize(query);
  return accessibleAimeMenus(router, serverAccessCodes).map(menu => {
    const label = menu.label.toLocaleLowerCase("id-ID");
    const haystack = `${label} ${menu.route.replace(/[\/_-]+/g, " ")} ${menu.keywords}`.toLocaleLowerCase("id-ID");
    let score = label === query ? 100 : (label.indexOf(query) !== -1 && query.length > 2 ? 35 : 0);
    tokens.forEach(token => {
      if (label.indexOf(token) !== -1) score += 10;
      else if (haystack.indexOf(token) !== -1) score += 4;
    });
    return { ...menu, score };
  }).filter(menu => menu.score > 0).sort((left, right) => right.score - left.score || left.label.localeCompare(right.label)).slice(0, limit);
}

export function contextualAimeMenus(router, currentPath, serverAccessCodes = null, limit = 3) {
  const current = String(currentPath || "/").split("?")[0];
  const root = current.split("/").filter(Boolean)[0] || "dashboard";
  const menus = accessibleAimeMenus(router, serverAccessCodes).filter(menu => !samePage(menu.route, current));
  const scored = menus.map(menu => {
    const candidateRoot = menu.route.split("/").filter(Boolean)[0] || "";
    let score = candidateRoot === root ? 30 : 0;
    if (current === "/dashboard" && ["/appointment/todays", "/patient/list", "/queue"].indexOf(menu.route) !== -1) score += 25;
    if (menu.route === "/guide") score += 2;
    if (/\/(?:add|edit)(?:\/|$)/.test(menu.route)) score -= 3;
    return { ...menu, score };
  }).sort((left, right) => right.score - left.score || left.label.localeCompare(right.label));
  const relevant = scored.filter(menu => menu.score > 0);
  return (relevant.length ? relevant : scored).slice(0, limit);
}
